import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { translations } from "./translations";
import { CookieBanner } from "./LegalComponents";

// Service slug mappings for multi-language URLs
const serviceSlugMappings = {
  'sirket-kurulumu': { tr: 'sirket-kurulumu', en: 'company-formation', es: 'constitucion-empresa' },
  'gayrimenkul-yatirim-danismanligi': { tr: 'gayrimenkul-yatirim-danismanligi', en: 'real-estate-investment-consulting', es: 'consultoria-inversion-inmobiliaria' },
  'oturum-izni': { tr: 'oturum-izni', en: 'residence-permit', es: 'permiso-residencia' },
  'kapsamli-danismanlik': { tr: 'kapsamli-danismanlik', en: 'comprehensive-consulting', es: 'consultoria-integral' },
  'vergi-danismanligi': { tr: 'vergi-danismanligi', en: 'tax-consulting', es: 'consultoria-fiscal' },
  'pilotaj-egitimi': { tr: 'pilotaj-egitimi', en: 'pilot-training', es: 'formacion-piloto' }
};

// Get canonical ID from any language slug
const getCanonicalIdFromSlug = (slug) => {
  // Check if it's already a canonical ID (TR slug)
  if (serviceSlugMappings[slug]) return slug;
  
  // Search through all mappings
  for (const [canonicalId, slugs] of Object.entries(serviceSlugMappings)) {
    if (Object.values(slugs).includes(slug)) {
      return canonicalId;
    }
  }
  return slug; // fallback
};

// Get localized slug for a specific language
const getLocalizedSlug = (canonicalId, lang) => {
  const mapping = serviceSlugMappings[canonicalId];
  if (mapping && mapping[lang]) {
    return mapping[lang];
  }
  return canonicalId;
};

// Find slug mapping and detect language from URL
const findSlugMapping = (slug) => {
  for (const [canonicalId, langSlugs] of Object.entries(serviceSlugMappings)) {
    for (const [lang, langSlug] of Object.entries(langSlugs)) {
      if (langSlug === slug) {
        return {
          canonicalId,
          currentLang: lang,
          allSlugs: langSlugs
        };
      }
    }
  }
  return null;
};

// Logo URLs
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_event-booking-pro-7/artifacts/6r7gbjry_logo%20800x800.png";

// Social Media Icons
const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
);

const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
);

// Service detail content for each service - FULL TRANSLATIONS
export const serviceDetails = {
  tr: {
    "sirket-kurulumu": {
      title: "Şirket Kurulumu",
      subtitle: "İspanya'da Şirket Kurulumu: İşinizi Avrupa'nın Kalbinde Başlatın",
      intro: "İspanya, Avrupa Birliği'nin dördüncü büyük ekonomisi olarak girişimcilere ve yatırımcılara güçlü bir iş ortamı sunmaktadır. Stratejik coğrafi konumu, gelişmiş altyapısı ve nitelikli iş gücüyle İspanya, uluslararası iş dünyası için ideal bir üs niteliğindedir.",
      intro2: "Nova Iberian olarak, İspanya'da şirket kurma sürecinizi baştan sona yönetiyoruz. Hangi şirket yapısının sizin için en uygun olduğunu belirliyor, tüm yasal ve bürokratik süreçleri sizin adınıza takip ediyoruz.",
      sections: [
        {
          title: "Şirket Türleri",
          subsections: [
            {
              name: "Autónomo (Serbest Meslek - Freelancer Kaydı)",
              desc: "İspanya'da bireysel olarak çalışmak isteyen profesyoneller için en yaygın ve en hızlı başlangıç yoludur.",
              items: ["NIE numarası (yabancı kimlik numarası)", "İspanya'da vergi kaydı (Hacienda)", "Sosyal güvenlik kaydı", "Aylık sosyal güvenlik prim ödemeleri"],
              note: "Autónomo statüsü özellikle danışmanlar, yazılımcılar, tasarımcılar, tercümanlar ve dijital nomadlar için idealdir."
            },
            {
              name: "Sociedad Limitada (SL) - Limited Şirket",
              desc: "İspanya'daki en popüler şirket türüdür ve Türkiye'deki limited şirkete benzer bir yapıya sahiptir.",
              items: ["Minimum 3.000 € sermaye", "Şirket ana sözleşmesinin noter tarafından onaylanması", "Ticaret siciline tescil", "Vergi ve sosyal güvenlik kaydı", "Şirket banka hesabı açılması"],
              note: "SL yapısı, özellikle birden fazla ortağı olan işletmeler için uygundur."
            },
            {
              name: "Yabancı Şirket Şubesi (Sucursal)",
              desc: "Ana şirketiniz başka bir ülkede kayıtlıysa, İspanya'da bir şube açarak faaliyetlerinizi genişletebilirsiniz.",
              items: ["Ana şirketin kuruluş belgelerinin apostilli ve yeminli tercüme edilmiş kopyaları", "İspanya ticaret siciline tescil", "Vergi ve sosyal güvenlik kayıtları"]
            }
          ]
        },
        {
          title: "Sürecimiz",
          steps: [
            { num: "1", title: "İhtiyaç Analizi", desc: "İş modelinizi, hedeflerinizi ve bütçenizi değerlendiriyoruz." },
            { num: "2", title: "Yapı Önerisi", desc: "Autónomo, SL veya şube arasında sizin için en uygun yapıyı belirliyoruz." },
            { num: "3", title: "Belge Hazırlığı", desc: "Tüm gerekli evrakları hazırlıyor ve denetliyoruz." },
            { num: "4", title: "Resmi Kayıtlar", desc: "Vergi dairesi, ticaret sicili ve sosyal güvenlik kayıtlarını tamamlıyoruz." },
            { num: "5", title: "Operasyonel Başlangıç", desc: "Banka hesabı açılışı, muhasebe sisteminin kurulması." }
          ]
        },
        {
          title: "Neden Nova Iberian?",
          benefits: ["Türkçe, İngilizce ve İspanyolca hizmet", "Vergi danışmanlığı ile entegre süreç yönetimi", "Oturum izni ve şirket kurulumu koordinasyonu", "Kuruluş sonrası sürekli muhasebe ve uyumluluk desteği"]
        }
      ],
      metaTitle: "İspanya'da Şirket Kurulumu | Autónomo & SL Kuruluşu | Nova Iberian",
      metaDesc: "İspanya'da şirket kurmak mı istiyorsunuz? Autónomo kaydı, Sociedad Limitada (SL) kuruluşu veya şube açılışı için uzman danışmanlık."
    },
    "gayrimenkul-yatirim-danismanligi": {
      title: "Gayrimenkul ve Yatırım Danışmanlığı",
      subtitle: "İspanya'da Gayrimenkul Yatırımı: Akdeniz'de Değerli Bir Gelecek",
      intro: "İspanya, Avrupa'nın en dinamik gayrimenkul piyasalarından birine sahiptir. Özellikle Barselona ve Katalonya bölgesi; muhteşem plajları, Akdeniz iklimi, zengin kültürel yaşamı ve güçlü kira getirisi potansiyeliyle uluslararası yatırımcıların ilk tercihlerinden biridir.",
      intro2: "Nova Iberian olarak, İspanya gayrimenkul piyasasındaki deneyimimizi sizinle paylaşıyoruz. İster kendi yaşam alanınız olsun, ister yatırım amaçlı bir mülk arayışında olun, size en uygun gayrimenkulü bulmak için buradayız.",
      sections: [
        {
          title: "Hizmetlerimiz",
          subsections: [
            {
              name: "Mülk Araştırma ve Seçimi",
              desc: "Barselona ve Katalonya genelinde lüks daireler, villalar ve yatırım projelerine erişim sağlıyoruz.",
              items: ["Lüks konut daireleri", "Müstakil villalar ve yazlık evler", "Yatırım amaçlı projeler", "Kısa dönem ve turistik kiralık mülkler"]
            },
            {
              name: "Due Diligence (Hukuki İnceleme)",
              desc: "Bir mülk satın almadan önce kapsamlı hukuki inceleme yapıyoruz:",
              items: ["Tapu ve mülkiyet kayıtlarının doğrulanması", "İpotek ve haciz kontrolü", "Belediye izin ve ruhsat durumlarının teyidi", "Vergi yükümlülüklerinin tespiti"]
            },
            {
              name: "Satın Alma Süreç Yönetimi",
              items: ["NIE numarası alınması", "İspanya'da banka hesabı açılması", "Satış sözleşmesinin hazırlanması ve incelenmesi", "Noter işlemleri ve tapu devri", "Vergi ve harç ödemelerinin koordinasyonu"]
            },
            {
              name: "Yatırım Danışmanlığı",
              desc: "İspanya gayrimenkul piyasasının trendlerini ve fırsatlarını analiz ederek size en yüksek getiri potansiyeline sahip yatırım seçeneklerini sunuyoruz.",
              items: ["Kira getirisi analizi", "Bölgesel piyasa değerlendirmesi", "Uzun vadeli yatırım stratejisi planlaması", "Portföy çeşitlendirme önerileri"]
            }
          ]
        },
        {
          title: "Neden Barselona ve Katalonya?",
          content: "Barselona, Avrupa'nın en çekici şehirlerinden biridir. Dünya standartlarında plajlar, zengin gastronomi kültürü, yılda 2.400 saatten fazla güneşli gün ve etkin ulaşım altyapısı ile hem yaşam hem de yatırım için mükemmel bir destinasyondur."
        },
        {
          title: "Sürecimiz",
          steps: [
            { num: "1", title: "İhtiyaç Belirleme", desc: "Bütçenizi, tercihlerinizi ve yatırım hedeflerinizi dinliyoruz." },
            { num: "2", title: "Mülk Sunumu", desc: "Kriterlerinize uygun seçenekleri araştırıyor ve sunuyoruz." },
            { num: "3", title: "Yerinde İnceleme", desc: "Mülk ziyaretleri ve bölge tanıtımı düzenliyoruz." },
            { num: "4", title: "Hukuki İnceleme", desc: "Due diligence sürecini yürütüyoruz." },
            { num: "5", title: "Satın Alma", desc: "Sözleşme, noter ve tapu süreçlerini yönetiyoruz." },
            { num: "6", title: "Sonrası", desc: "Mülk yönetimi, kiralama ve vergi konularında destek sağlıyoruz." }
          ]
        }
      ],
      metaTitle: "İspanya Gayrimenkul Yatırımı | Barselona Mülk Danışmanlığı | Nova Iberian",
      metaDesc: "Barselona ve Katalonya'da gayrimenkul yatırımı yapın. Lüks daireler, villalar ve yatırım projelerinde uzman danışmanlık."
    },
    "oturum-izni": {
      title: "Oturum İzni ve Vize Danışmanlığı",
      subtitle: "İspanya'da Yasal Olarak Yaşamak ve Çalışmak",
      intro: "İspanya, sunduğu çeşitli vize ve oturum izni seçenekleriyle dünyanın dört bir yanından insanları kendine çekmektedir. Dijital nomadlardan emeklilere, öğrencilerden ailelerini bir araya getirmek isteyenlere kadar herkes için uygun bir yasal yol bulunmaktadır.",
      intro2: "Nova Iberian olarak, İspanya göçmenlik hukukundaki uzmanlığımızla sizin için en doğru vize türünü belirliyor ve başvuru sürecinizi eksiksiz yönetiyoruz.",
      sections: [
        {
          title: "Dijital Nomad Vizesi",
          subtitle: "İspanya'dan Uzaktan Çalışın, Akdeniz Yaşamının Keyfini Sürün",
          content: "İspanya'nın Startup Yasası kapsamında sunulan Dijital Nomad Vizesi, yurt dışındaki bir şirket için uzaktan çalışan profesyonellere İspanya'da yasal olarak yaşama hakkı tanımaktadır.",
          highlights: [
            { label: "İşlem süresi", value: "20 iş günü" },
            { label: "Süre", value: "3 yıl" },
            { label: "Aile üyeleri", value: "Dahil edilebilir" },
            { label: "Vergi avantajı", value: "Beckham Yasası kapsamında" }
          ],
          requirements: ["En az 1 yıllık şirkette, en az 3 aydır çalışan profesyoneller", "Gelirinin en az %80'i İspanya dışından elde edenler", "Aylık minimum gelir: SMI'nin %200'ü (yaklaşık 2.763 €/ay)", "Üniversite diploması veya alanında en az 3 yıl deneyim", "Tam kapsamlı özel sağlık sigortası"],
          note: "Dijital Nomad Vizesi sahipleri Beckham Yasası'ndan yararlanabilir. Sabit %24 gelir vergisi, 6 yıl süreyle geçerlidir."
        },
        {
          title: "Non-Lucrative Vize (Kazanç Amaçsız Oturum İzni)",
          subtitle: "Çalışmadan İspanya'da Yaşamın Tadını Çıkarın",
          content: "Non-Lucrative Vize, İspanya'da çalışmadan yaşamak isteyenler için tasarlanmış bir oturum iznidir. Genellikle emekliler, tasarruf sahibi bireyler veya pasif geliri olan kişiler tarafından tercih edilmektedir.",
          highlights: [
            { label: "İşlem süresi", value: "Yaklaşık 1 ay" },
            { label: "İlk süre", value: "1 yıl" },
            { label: "Başvuru", value: "Ülkenizdeki İspanya konsolosluğundan" },
            { label: "Yenileme", value: "2'şer yıllık dönemlerle" }
          ],
          requirements: ["Banka hesabında IPREM'in %400'ü (yaklaşık 28.800 €)", "Aile üyeleri için kişi başı ek IPREM'in %100'ü", "6-12 aylık banka hesap dökümleri", "Tam kapsamlı özel sağlık sigortası"],
          note: "5 yıl sonunda kalıcı oturum hakkı kazanılır."
        },
        {
          title: "Öğrenci ve Eğitim Vizesi",
          subtitle: "İspanya'da Eğitim Alırken Avrupa Deneyimi Kazanın",
          content: "İspanya, dünya standartlarında üniversiteleri, dil okulları ve mesleki eğitim programlarıyla uluslararası öğrenciler için çekici bir destinasyondur.",
          highlights: [
            { label: "Eğitim programı", value: "Haftalık en az 20 saat" },
            { label: "Çalışma izni", value: "Haftada 30 saate kadar" },
            { label: "Finansal gereksinim", value: "Yıllık yaklaşık 7.200 €" }
          ],
          requirements: ["Akredite bir eğitim kurumuna kabul belgesi", "Eğitim süresi boyunca aylık IPREM'in %100'ü tutarında mali kaynak", "Özel sağlık sigortası"],
          benefits: ["Üniversite öğrencileri haftada 30 saate kadar çalışabilir", "Mezuniyet sonrası çalışma iznine geçiş yapılabilir", "2 yıla kadar iş arama vizesi hakkı"]
        },
        {
          title: "Aile Birleşimi Vizesi",
          subtitle: "Ailenizi İspanya'da Bir Araya Getirin",
          content: "İspanya'da yasal olarak yaşıyorsanız ve oturma izninizi yenilediyseniz, aile üyelerinizi yanınıza getirebilirsiniz.",
          highlights: [
            { label: "Şart", value: "En az 1 yıl yasal ikamet + yenilenmiş oturum izni" },
            { label: "Kapsam", value: "Eş, 18 yaş altı çocuklar, 65 yaş üstü bağımlı ebeveynler" },
            { label: "Çalışma izni", value: "Aile üyeleri için derhal geçerli" }
          ],
          requirements: ["Konutun belediye tarafından uygunluk raporu", "Gelir: İlk aile üyesi için IPREM'in %150'si", "Özel sağlık sigortası"]
        },
        {
          title: "İspanya Vatandaşlığı",
          subtitle: "İspanyol Vatandaşı Olun, AB Pasaportu Kazanın",
          content: "İspanya'da yeterli süre yasal olarak ikamet ettikten sonra vatandaşlık başvurusu yapabilirsiniz.",
          highlights: [
            { label: "Standart yol", value: "10 yıl kesintisiz yasal ikamet" },
            { label: "Latin Amerikalılar", value: "2 yıl" },
            { label: "İspanyol ile evlilik", value: "1 yıl" }
          ],
          requirements: ["DELE A2: İspanyolca dil yeterlilik sınavı", "CCSE: İspanya anayasası ve kültürel bilgi sınavı"]
        }
      ],
      metaTitle: "İspanya Oturum İzni ve Vize | Dijital Nomad, Non-Lucrative | Nova Iberian",
      metaDesc: "İspanya'da oturum izni almak için uzman danışmanlık. Dijital Nomad Vizesi, Non-Lucrative Vize, Öğrenci Vizesi ve Aile Birleşimi."
    },
    "kapsamli-danismanlik": {
      title: "Kapsamlı Danışmanlık",
      subtitle: "İspanya'ya Taşınma Sürecinizi Bütünsel Olarak Yönetiyoruz",
      intro: "İspanya'ya taşınmak, tek bir konuya odaklanmaktan çok daha fazlasını gerektirir. Oturum izni, vergi planlaması, gayrimenkul yatırımı, şirket kurulumu, okul seçimi ve günlük yaşam düzenlemeleri gibi birbirine bağlı pek çok sürecin koordineli bir şekilde yönetilmesi gerekmektedir.",
      intro2: "Nova Iberian'ın Kapsamlı Danışmanlık hizmeti, tüm bu süreçleri tek bir çatı altında birleştiren bütünsel bir yaklaşım sunar.",
      sections: [
        {
          title: "Ne Sunuyoruz?",
          subsections: [
            {
              name: "Taşınma Öncesi Planlama",
              desc: "İspanya'ya taşınmadan önce ihtiyaçlarınızı, bütçenizi ve özel koşullarınızı değerlendirerek size özel bir yol haritası oluşturuyoruz."
            },
            {
              name: "Çoklu Süreç Koordinasyonu",
              desc: "Oturum izni türünüz vergi rejiminizi, gayrimenkul yatırımınız vize başvurunuzu, şirket kurulumunuz çalışma izninizi doğrudan etkiler. Biz bu bağlantıları baştan kurarak süreçlerin birbirini desteklemesini sağlıyoruz."
            }
          ]
        },
        {
          title: "Hizmet Alanları",
          items: ["Vize ve oturum izni stratejisi", "Vergi planlaması ve Beckham Yasası değerlendirmesi", "Gayrimenkul araştırma ve yatırım danışmanlığı", "Şirket kurulumu ve iş yapısı seçimi", "Aile için okul araştırması", "Bölge tanıtımı ve yaşam alanı seçimi", "Banka hesabı açılışı ve mali altyapı kurulumu", "Sağlık sigortası seçimi", "Belediye kaydı (Empadronamiento) ve idari işlemler"]
        },
        {
          title: "Kimler İçin?",
          items: ["İspanya'ya taşınmayı planlayan bireyler ve aileler", "Birden fazla hizmet alanında eşzamanlı desteğe ihtiyaç duyanlar", "Süreçlerin karmaşıklığı karşısında tek bir muhatap arayanlar", "İspanya'yı henüz keşfetmeye başlayan ve nereden başlayacağını bilmeyenler"]
        },
        {
          title: "Sürecimiz",
          steps: [
            { num: "1", title: "Tanışma Görüşmesi", desc: "Durumunuzu, hedeflerinizi ve önceliklerinizi dinliyoruz." },
            { num: "2", title: "Strateji Oluşturma", desc: "Size özel bir yol haritası ve zaman çizelgesi hazırlıyoruz." },
            { num: "3", title: "Eşzamanlı Yürütme", desc: "İlgili tüm süreçleri koordineli olarak başlatıyoruz." },
            { num: "4", title: "Düzenli Raporlama", desc: "Her aşamada sizi bilgilendiriyor, sonraki adımları netleştiriyoruz." },
            { num: "5", title: "Yerleşim Sonrası", desc: "İspanya'daki yeni yaşamınızda ihtiyaç duyduğunuz sürekli destek." }
          ]
        }
      ],
      metaTitle: "İspanya'ya Taşınma Danışmanlığı | Kapsamlı Relocation | Nova Iberian",
      metaDesc: "İspanya'ya taşınma sürecinizin tamamını yönetiyoruz. Vize, vergi, gayrimenkul, şirket kurulumu tek çatı altında."
    },
    "vergi-danismanligi": {
      title: "Vergi Danışmanlığı ve Planlaması",
      subtitle: "İspanya'da Vergi Avantajlarınızı Maksimize Edin",
      intro: "İspanya'nın vergi sistemi, doğru planlama yapıldığında önemli avantajlar sunmaktadır. Özellikle yeni yerleşenler için Beckham Yasası gibi özel düzenlemeler, vergi yükünü önemli ölçüde azaltabilir.",
      intro2: "Nova Iberian olarak, İspanya'daki vergi danışmanlığı hizmetimizle mali durumunuzu optimize ediyor, yasal yükümlülüklerinizi eksiksiz yerine getirmenizi sağlıyoruz.",
      sections: [
        {
          title: "Hizmetlerimiz",
          subsections: [
            {
              name: "Beckham Yasası (Özel Vergi Rejimi)",
              desc: "İspanya'ya yeni taşınan kişiler için sunulan en önemli vergi avantajıdır. Normal koşullarda %47'ye kadar çıkabilen gelir vergisi oranı yerine sabit %24 vergi uygulanır.",
              items: ["Kapsam: 600.000 €'ya kadar gelir", "Süre: 6 yıl", "Koşul: Son 5 yılda İspanya'da vergi mükellefi olmamak", "Dijital Nomad Vizesi sahipleri için geçerli"]
            },
            {
              name: "Gelir Vergisi Beyannamesi (IRPF)",
              desc: "İspanya'da vergi mükellefi olan herkes yıllık gelir vergisi beyannamesi vermekle yükümlüdür.",
              items: ["Beyanname hazırlığı ve kontrolü", "Vergi indirimleri ve muafiyetlerin değerlendirilmesi", "Zamanında beyanname sunumu"]
            },
            {
              name: "Kurumlar Vergisi",
              desc: "İspanya'da faaliyet gösteren şirketler net kârları üzerinden %25 oranında kurumlar vergisi ödemektedir.",
              items: ["Üç aylık taksit ödemeleri", "Yıllık kurumlar vergisi beyannamesi", "Vergi planlaması ve optimizasyonu"]
            },
            {
              name: "KDV (IVA) Hizmetleri",
              desc: "İspanya'da standart KDV oranı %21'dir.",
              items: ["KDV kaydı", "Üç aylık KDV beyannameleri", "Yıllık KDV özet beyannamesi", "KDV iade süreçleri"]
            }
          ]
        },
        {
          title: "Neden Nova Iberian?",
          benefits: ["Oturum izni süreciyle entegre vergi planlaması", "Beckham Yasası başvuru ve takip desteği", "Türkçe, İngilizce ve İspanyolca hizmet", "Uluslararası vergi konularında deneyim", "Sürekli muhasebe ve uyumluluk desteği"]
        }
      ],
      metaTitle: "İspanya Vergi Danışmanlığı | Beckham Yasası | Nova Iberian",
      metaDesc: "İspanya'da vergi avantajlarından yararlanın. Beckham Yasası, gelir vergisi, kurumlar vergisi ve KDV danışmanlığı."
    },
    "pilotaj-egitimi": {
      title: "Pilotaj Eğitimi Danışmanlığı",
      subtitle: "İspanya'da Pilotaj Eğitimi: Gökyüzüne Giden Yolunuz",
      intro: "İspanya, yılın büyük bölümünde güneşli havası, modern uçuş okulları ve Avrupa Havacılık Güvenliği Ajansı (EASA) standartlarında eğitim veren akredite kurumlarıyla pilotaj eğitimi için ideal bir destinasyondur.",
      intro2: "EASA lisansları, tüm Avrupa Birliği ülkelerinde geçerli olup uluslararası bir havacılık kariyerinin kapılarını açmaktadır.",
      sections: [
        {
          title: "Hizmetlerimiz",
          subsections: [
            {
              name: "Uçuş Okulu Araştırma ve Seçimi",
              desc: "İspanya'daki EASA onaylı uçuş okullarını araştırıyor, eğitim programlarını karşılaştırıyor ve ihtiyaçlarınıza en uygun okulu belirlemenize yardımcı oluyoruz.",
              items: ["Okul akreditasyon ve güvenlik kayıtlarının incelenmesi", "Eğitim müfredatı ve uçak filosu değerlendirmesi", "Maliyet karşılaştırması ve ödeme planı bilgisi", "Mezun başarı oranları ve kariyer destek programları"]
            },
            {
              name: "Eğitim Programları",
              items: ["PPL (Private Pilot License – Özel Pilot Lisansı)", "CPL (Commercial Pilot License – Ticari Pilot Lisansı)", "ATPL (Airline Transport Pilot License – Havayolu Taşımacılık Pilot Lisansı)", "Tip derecelendirme eğitimleri"]
            },
            {
              name: "Vize ve Oturum İzni Desteği",
              desc: "Pilotaj eğitimi için İspanya'ya gelecek adaylara uygun vize türünü belirliyor ve başvuru sürecinde destek sağlıyoruz."
            },
            {
              name: "İspanya'da Yaşam Düzenlemeleri",
              items: ["Konut araştırması", "Sağlık sigortası seçimi", "Bölge tanıtımı ve oryantasyon", "Banka hesabı açılışı ve idari işlemler"]
            }
          ]
        },
        {
          title: "Neden İspanya'da Pilotaj Eğitimi?",
          benefits: ["Yılda 300 günden fazla uçuşa uygun hava koşulları", "EASA standartlarında eğitim – tüm AB ülkelerinde geçerli lisans", "Avrupa'nın en rekabetçi eğitim maliyetleri arasında", "Çeşitli hava koşulları ve coğrafyalarda uçuş deneyimi", "Yüksek yaşam kalitesi ve güvenli ortam"]
        },
        {
          title: "Sürecimiz",
          steps: [
            { num: "1", title: "Değerlendirme", desc: "Havacılık hedeflerinizi, bütçenizi ve zaman planınızı dinliyoruz." },
            { num: "2", title: "Okul Eşleştirme", desc: "Size en uygun uçuş okullarını sunuyoruz." },
            { num: "3", title: "Başvuru Desteği", desc: "Okul kaydı ve vize başvuru süreçlerinde rehberlik ediyoruz." },
            { num: "4", title: "Yerleşim", desc: "İspanya'daki konut, sigorta ve idari işlemlerinizi koordine ediyoruz." },
            { num: "5", title: "Takip", desc: "Eğitim süresince ihtiyaç duyduğunuz destek için yanınızdayız." }
          ]
        }
      ],
      metaTitle: "İspanya Pilotaj Eğitimi | EASA Lisans | Nova Iberian",
      metaDesc: "İspanya'da pilotaj eğitimi almak mı istiyorsunuz? EASA onaylı uçuş okulları, PPL, CPL, ATPL programları."
    }
  },
  en: {
    "sirket-kurulumu": {
      title: "Company Formation",
      subtitle: "Company Formation in Spain: Start Your Business in the Heart of Europe",
      intro: "Spain, as the fourth largest economy in the European Union, offers a strong business environment for entrepreneurs and investors. With its strategic geographical location, developed infrastructure and qualified workforce, Spain is an ideal base for international business.",
      intro2: "At Nova Iberian, we manage your company formation process in Spain from start to finish. We determine which company structure is most suitable for you and handle all legal and bureaucratic processes on your behalf.",
      sections: [
        {
          title: "Company Types",
          subsections: [
            {
              name: "Autónomo (Self-Employed / Freelancer Registration)",
              desc: "This is the most common and fastest way to start for professionals who want to work individually in Spain.",
              items: ["NIE number (foreigner identification number)", "Tax registration in Spain (Hacienda)", "Social security registration", "Monthly social security contributions"],
              note: "Autónomo status is ideal for consultants, software developers, designers, translators and digital nomads."
            },
            {
              name: "Sociedad Limitada (SL) - Limited Company",
              desc: "This is the most popular company type in Spain and has a structure similar to a limited liability company.",
              items: ["Minimum €3,000 capital", "Notarization of company articles of association", "Commercial registry registration", "Tax and social security registration", "Company bank account opening"],
              note: "SL structure is suitable for businesses with multiple partners."
            },
            {
              name: "Foreign Company Branch (Sucursal)",
              desc: "If your parent company is registered in another country, you can expand your operations by opening a branch in Spain.",
              items: ["Apostilled and sworn translated copies of parent company incorporation documents", "Registration in Spanish commercial registry", "Tax and social security registrations"]
            }
          ]
        },
        {
          title: "Our Process",
          steps: [
            { num: "1", title: "Needs Analysis", desc: "We evaluate your business model, goals and budget." },
            { num: "2", title: "Structure Recommendation", desc: "We determine the most suitable structure for you among Autónomo, SL or branch." },
            { num: "3", title: "Document Preparation", desc: "We prepare and review all required documents." },
            { num: "4", title: "Official Registrations", desc: "We complete tax office, commercial registry and social security registrations." },
            { num: "5", title: "Operational Start", desc: "Bank account opening, accounting system setup." }
          ]
        },
        {
          title: "Why Nova Iberian?",
          benefits: ["Service in Turkish, English and Spanish", "Integrated process management with tax consulting", "Residence permit and company formation coordination", "Continuous accounting and compliance support after establishment"]
        }
      ],
      metaTitle: "Company Formation in Spain | Autónomo & SL Setup | Nova Iberian",
      metaDesc: "Want to start a company in Spain? Expert consulting for Autónomo registration, Sociedad Limitada (SL) formation or branch opening."
    },
    "gayrimenkul-yatirim-danismanligi": {
      title: "Real Estate & Investment Consulting",
      subtitle: "Real Estate Investment in Spain: A Valuable Future in the Mediterranean",
      intro: "Spain has one of Europe's most dynamic real estate markets. Barcelona and Catalonia in particular are among the first choices of international investors with their stunning beaches, Mediterranean climate, rich cultural life and strong rental yield potential.",
      intro2: "At Nova Iberian, we share our experience in the Spanish real estate market with you. Whether you're looking for your own living space or an investment property, we're here to find the most suitable real estate for you.",
      sections: [
        {
          title: "Our Services",
          subsections: [
            {
              name: "Property Research and Selection",
              desc: "We provide access to luxury apartments, villas and investment projects throughout Barcelona and Catalonia.",
              items: ["Luxury residential apartments", "Detached villas and summer houses", "Investment projects", "Short-term and tourist rental properties"]
            },
            {
              name: "Due Diligence (Legal Review)",
              desc: "We conduct comprehensive legal review before purchasing a property:",
              items: ["Verification of title and ownership records", "Mortgage and lien check", "Confirmation of municipal permits and licenses", "Determination of tax obligations"]
            },
            {
              name: "Purchase Process Management",
              items: ["Obtaining NIE number", "Opening a bank account in Spain", "Preparation and review of sales contract", "Notary procedures and title transfer", "Coordination of tax and fee payments"]
            },
            {
              name: "Investment Consulting",
              desc: "By analyzing trends and opportunities in the Spanish real estate market, we offer you investment options with the highest return potential.",
              items: ["Rental yield analysis", "Regional market assessment", "Long-term investment strategy planning", "Portfolio diversification recommendations"]
            }
          ]
        },
        {
          title: "Why Barcelona and Catalonia?",
          content: "Barcelona is one of Europe's most attractive cities. With world-class beaches, rich gastronomic culture, more than 2,400 hours of sunshine per year and effective transportation infrastructure, it is a perfect destination for both living and investment."
        },
        {
          title: "Our Process",
          steps: [
            { num: "1", title: "Needs Assessment", desc: "We listen to your budget, preferences and investment goals." },
            { num: "2", title: "Property Presentation", desc: "We research and present options that meet your criteria." },
            { num: "3", title: "On-Site Inspection", desc: "We arrange property visits and area introductions." },
            { num: "4", title: "Legal Review", desc: "We conduct the due diligence process." },
            { num: "5", title: "Purchase", desc: "We manage contract, notary and title procedures." },
            { num: "6", title: "After Purchase", desc: "We provide support on property management, rental and tax matters." }
          ]
        }
      ],
      metaTitle: "Spain Real Estate Investment | Barcelona Property Consulting | Nova Iberian",
      metaDesc: "Invest in real estate in Barcelona and Catalonia. Expert consulting on luxury apartments, villas and investment projects."
    },
    "oturum-izni": {
      title: "Residence Permit & Visa Consulting",
      subtitle: "Live and Work Legally in Spain",
      intro: "Spain attracts people from all over the world with its various visa and residence permit options. From digital nomads to retirees, from students to those who want to bring their families together, there is a suitable legal path for everyone.",
      intro2: "At Nova Iberian, with our expertise in Spanish immigration law, we determine the right visa type for you and manage your application process completely.",
      sections: [
        {
          title: "Digital Nomad Visa",
          subtitle: "Work Remotely from Spain, Enjoy Mediterranean Life",
          content: "The Digital Nomad Visa offered under Spain's Startup Law grants professionals working remotely for a company abroad the right to live legally in Spain.",
          highlights: [
            { label: "Processing time", value: "20 business days" },
            { label: "Duration", value: "3 years" },
            { label: "Family members", value: "Can be included" },
            { label: "Tax advantage", value: "Under Beckham Law" }
          ],
          requirements: ["Professionals working at least 3 months in a company with at least 1 year history", "Those earning at least 80% of income outside Spain", "Minimum monthly income: 200% of SMI (approximately €2,763/month)", "University degree or at least 3 years experience in the field", "Comprehensive private health insurance"],
          note: "Digital Nomad Visa holders can benefit from the Beckham Law. Fixed 24% income tax, valid for 6 years."
        },
        {
          title: "Non-Lucrative Visa (Non-Profit Residence Permit)",
          subtitle: "Enjoy Living in Spain Without Working",
          content: "The Non-Lucrative Visa is a residence permit designed for those who want to live in Spain without working. Generally preferred by retirees, individuals with savings or those with passive income.",
          highlights: [
            { label: "Processing time", value: "Approximately 1 month" },
            { label: "Initial period", value: "1 year" },
            { label: "Application", value: "From Spanish consulate in your country" },
            { label: "Renewal", value: "In 2-year periods" }
          ],
          requirements: ["400% of IPREM in bank account (approximately €28,800)", "Additional 100% of IPREM per family member", "6-12 months bank statements", "Comprehensive private health insurance"],
          note: "Permanent residence rights are acquired after 5 years."
        },
        {
          title: "Student and Education Visa",
          subtitle: "Gain European Experience While Studying in Spain",
          content: "Spain is an attractive destination for international students with its world-class universities, language schools and vocational training programs.",
          highlights: [
            { label: "Education program", value: "At least 20 hours per week" },
            { label: "Work permit", value: "Up to 30 hours per week" },
            { label: "Financial requirement", value: "Approximately €7,200 annually" }
          ],
          requirements: ["Acceptance letter from an accredited educational institution", "Financial resources equivalent to 100% of IPREM monthly during education", "Private health insurance"],
          benefits: ["University students can work up to 30 hours per week", "Can transition to work permit after graduation", "Right to job search visa for up to 2 years"]
        },
        {
          title: "Family Reunification Visa",
          subtitle: "Bring Your Family Together in Spain",
          content: "If you are legally living in Spain and have renewed your residence permit, you can bring your family members to join you.",
          highlights: [
            { label: "Requirement", value: "At least 1 year legal residence + renewed residence permit" },
            { label: "Coverage", value: "Spouse, children under 18, dependent parents over 65" },
            { label: "Work permit", value: "Valid immediately for family members" }
          ],
          requirements: ["Housing adequacy report from municipality", "Income: 150% of IPREM for first family member", "Private health insurance"]
        },
        {
          title: "Spanish Citizenship",
          subtitle: "Become a Spanish Citizen, Gain an EU Passport",
          content: "You can apply for citizenship after legally residing in Spain for a sufficient period.",
          highlights: [
            { label: "Standard path", value: "10 years of uninterrupted legal residence" },
            { label: "Latin Americans", value: "2 years" },
            { label: "Marriage to Spanish", value: "1 year" }
          ],
          requirements: ["DELE A2: Spanish language proficiency exam", "CCSE: Spanish constitution and cultural knowledge exam"]
        }
      ],
      metaTitle: "Spain Residence Permit & Visa | Digital Nomad, Non-Lucrative | Nova Iberian",
      metaDesc: "Expert consulting for residence permit in Spain. Digital Nomad Visa, Non-Lucrative Visa, Student Visa and Family Reunification."
    },
    "kapsamli-danismanlik": {
      title: "Comprehensive Consulting",
      subtitle: "We Manage Your Relocation to Spain Holistically",
      intro: "Moving to Spain requires much more than focusing on a single topic. Many interconnected processes such as residence permits, tax planning, real estate investment, company formation, school selection and daily life arrangements need to be managed in a coordinated manner.",
      intro2: "Nova Iberian's Comprehensive Consulting service offers a holistic approach that combines all these processes under one roof.",
      sections: [
        {
          title: "What We Offer",
          subsections: [
            {
              name: "Pre-Move Planning",
              desc: "We create a customized roadmap by evaluating your needs, budget and special conditions before moving to Spain."
            },
            {
              name: "Multi-Process Coordination",
              desc: "Your residence permit type directly affects your tax regime, your real estate investment affects your visa application, your company formation affects your work permit. We establish these connections from the start to ensure processes support each other."
            }
          ]
        },
        {
          title: "Service Areas",
          items: ["Visa and residence permit strategy", "Tax planning and Beckham Law assessment", "Real estate research and investment consulting", "Company formation and business structure selection", "School research for family", "Area introduction and living space selection", "Bank account opening and financial infrastructure setup", "Health insurance selection", "Municipal registration (Empadronamiento) and administrative procedures"]
        },
        {
          title: "Who Is It For?",
          items: ["Individuals and families planning to move to Spain", "Those who need simultaneous support in multiple service areas", "Those looking for a single point of contact against the complexity of processes", "Those who are just starting to explore Spain and don't know where to begin"]
        },
        {
          title: "Our Process",
          steps: [
            { num: "1", title: "Introduction Meeting", desc: "We listen to your situation, goals and priorities." },
            { num: "2", title: "Strategy Development", desc: "We prepare a customized roadmap and timeline for you." },
            { num: "3", title: "Simultaneous Execution", desc: "We start all relevant processes in a coordinated manner." },
            { num: "4", title: "Regular Reporting", desc: "We keep you informed at every stage and clarify next steps." },
            { num: "5", title: "Post-Settlement", desc: "Continuous support you need in your new life in Spain." }
          ]
        }
      ],
      metaTitle: "Spain Relocation Consulting | Comprehensive Relocation | Nova Iberian",
      metaDesc: "We manage your entire relocation process to Spain. Visa, tax, real estate, company formation under one roof."
    },
    "vergi-danismanligi": {
      title: "Tax Consulting & Planning",
      subtitle: "Maximize Your Tax Advantages in Spain",
      intro: "Spain's tax system offers significant advantages when properly planned. Special regulations such as the Beckham Law can significantly reduce the tax burden, especially for new residents.",
      intro2: "At Nova Iberian, with our tax consulting service in Spain, we optimize your financial situation and ensure you fully meet your legal obligations.",
      sections: [
        {
          title: "Our Services",
          subsections: [
            {
              name: "Beckham Law (Special Tax Regime)",
              desc: "This is the most important tax advantage offered to people newly moving to Spain. Instead of income tax rates that can reach up to 47% under normal conditions, a flat 24% tax applies.",
              items: ["Coverage: Income up to €600,000", "Duration: 6 years", "Condition: Not being a tax resident in Spain in the last 5 years", "Valid for Digital Nomad Visa holders"]
            },
            {
              name: "Income Tax Return (IRPF)",
              desc: "Everyone who is a tax resident in Spain is required to file an annual income tax return.",
              items: ["Return preparation and review", "Evaluation of tax deductions and exemptions", "Timely return submission"]
            },
            {
              name: "Corporate Tax",
              desc: "Companies operating in Spain pay corporate tax at a rate of 25% on their net profits.",
              items: ["Quarterly installment payments", "Annual corporate tax return", "Tax planning and optimization"]
            },
            {
              name: "VAT (IVA) Services",
              desc: "The standard VAT rate in Spain is 21%.",
              items: ["VAT registration", "Quarterly VAT returns", "Annual VAT summary return", "VAT refund processes"]
            }
          ]
        },
        {
          title: "Why Nova Iberian?",
          benefits: ["Tax planning integrated with residence permit process", "Beckham Law application and follow-up support", "Service in Turkish, English and Spanish", "Experience in international tax matters", "Continuous accounting and compliance support"]
        }
      ],
      metaTitle: "Spain Tax Consulting | Beckham Law | Nova Iberian",
      metaDesc: "Benefit from tax advantages in Spain. Beckham Law, income tax, corporate tax and VAT consulting."
    },
    "pilotaj-egitimi": {
      title: "Pilot Training Consulting",
      subtitle: "Pilot Training in Spain: Your Path to the Sky",
      intro: "Spain is an ideal destination for pilot training with its sunny weather for most of the year, modern flight schools and accredited institutions offering training to European Aviation Safety Agency (EASA) standards.",
      intro2: "EASA licenses are valid in all European Union countries and open the doors to an international aviation career.",
      sections: [
        {
          title: "Our Services",
          subsections: [
            {
              name: "Flight School Research and Selection",
              desc: "We research EASA approved flight schools in Spain, compare training programs and help you identify the most suitable school for your needs.",
              items: ["Review of school accreditation and safety records", "Training curriculum and aircraft fleet evaluation", "Cost comparison and payment plan information", "Graduate success rates and career support programs"]
            },
            {
              name: "Training Programs",
              items: ["PPL (Private Pilot License)", "CPL (Commercial Pilot License)", "ATPL (Airline Transport Pilot License)", "Type rating training"]
            },
            {
              name: "Visa and Residence Permit Support",
              desc: "We determine the appropriate visa type for candidates coming to Spain for pilot training and provide support during the application process."
            },
            {
              name: "Life Arrangements in Spain",
              items: ["Housing research", "Health insurance selection", "Area introduction and orientation", "Bank account opening and administrative procedures"]
            }
          ]
        },
        {
          title: "Why Pilot Training in Spain?",
          benefits: ["More than 300 days of suitable flying weather per year", "EASA standard training – license valid in all EU countries", "Among Europe's most competitive training costs", "Flight experience in various weather conditions and geographies", "High quality of life and safe environment"]
        },
        {
          title: "Our Process",
          steps: [
            { num: "1", title: "Assessment", desc: "We listen to your aviation goals, budget and time plan." },
            { num: "2", title: "School Matching", desc: "We present the most suitable flight schools for you." },
            { num: "3", title: "Application Support", desc: "We provide guidance in school registration and visa application processes." },
            { num: "4", title: "Settlement", desc: "We coordinate your housing, insurance and administrative procedures in Spain." },
            { num: "5", title: "Follow-up", desc: "We are by your side for the support you need during training." }
          ]
        }
      ],
      metaTitle: "Spain Pilot Training | EASA License | Nova Iberian",
      metaDesc: "Want to get pilot training in Spain? EASA approved flight schools, PPL, CPL, ATPL programs."
    }
  },
  es: {
    "sirket-kurulumu": {
      title: "Constitución de Empresa",
      subtitle: "Constitución de Empresa en España: Inicie su Negocio en el Corazón de Europa",
      intro: "España, como la cuarta economía más grande de la Unión Europea, ofrece un entorno empresarial sólido para emprendedores e inversores. Con su ubicación geográfica estratégica, infraestructura desarrollada y mano de obra calificada, España es una base ideal para los negocios internacionales.",
      intro2: "En Nova Iberian, gestionamos su proceso de constitución de empresa en España de principio a fin. Determinamos qué estructura empresarial es más adecuada para usted y manejamos todos los procesos legales y burocráticos en su nombre.",
      sections: [
        {
          title: "Tipos de Empresa",
          subsections: [
            {
              name: "Autónomo (Trabajador por Cuenta Propia / Registro de Freelancer)",
              desc: "Esta es la forma más común y rápida de comenzar para profesionales que desean trabajar individualmente en España.",
              items: ["Número NIE (número de identificación de extranjero)", "Registro fiscal en España (Hacienda)", "Registro de seguridad social", "Contribuciones mensuales a la seguridad social"],
              note: "El estatus de autónomo es ideal para consultores, desarrolladores de software, diseñadores, traductores y nómadas digitales."
            },
            {
              name: "Sociedad Limitada (SL)",
              desc: "Este es el tipo de empresa más popular en España y tiene una estructura similar a una sociedad de responsabilidad limitada.",
              items: ["Capital mínimo de 3.000 €", "Notarización de los estatutos de la empresa", "Registro en el registro mercantil", "Registro fiscal y de seguridad social", "Apertura de cuenta bancaria de empresa"],
              note: "La estructura SL es adecuada para empresas con múltiples socios."
            },
            {
              name: "Sucursal de Empresa Extranjera",
              desc: "Si su empresa matriz está registrada en otro país, puede expandir sus operaciones abriendo una sucursal en España.",
              items: ["Copias apostilladas y traducidas juradas de los documentos de constitución de la empresa matriz", "Registro en el registro mercantil español", "Registros fiscales y de seguridad social"]
            }
          ]
        },
        {
          title: "Nuestro Proceso",
          steps: [
            { num: "1", title: "Análisis de Necesidades", desc: "Evaluamos su modelo de negocio, objetivos y presupuesto." },
            { num: "2", title: "Recomendación de Estructura", desc: "Determinamos la estructura más adecuada para usted entre Autónomo, SL o sucursal." },
            { num: "3", title: "Preparación de Documentos", desc: "Preparamos y revisamos todos los documentos requeridos." },
            { num: "4", title: "Registros Oficiales", desc: "Completamos los registros de la oficina de impuestos, registro mercantil y seguridad social." },
            { num: "5", title: "Inicio Operacional", desc: "Apertura de cuenta bancaria, configuración del sistema contable." }
          ]
        },
        {
          title: "¿Por qué Nova Iberian?",
          benefits: ["Servicio en turco, inglés y español", "Gestión de procesos integrada con consultoría fiscal", "Coordinación de permiso de residencia y constitución de empresa", "Soporte continuo de contabilidad y cumplimiento después del establecimiento"]
        }
      ],
      metaTitle: "Constitución de Empresa en España | Autónomo y SL | Nova Iberian",
      metaDesc: "¿Quiere crear una empresa en España? Asesoramiento experto para registro de Autónomo, constitución de Sociedad Limitada (SL) o apertura de sucursal."
    },
    "gayrimenkul-yatirim-danismanligi": {
      title: "Consultoría Inmobiliaria y de Inversión",
      subtitle: "Inversión Inmobiliaria en España: Un Futuro Valioso en el Mediterráneo",
      intro: "España tiene uno de los mercados inmobiliarios más dinámicos de Europa. Barcelona y Cataluña en particular se encuentran entre las primeras opciones de los inversores internacionales con sus impresionantes playas, clima mediterráneo, rica vida cultural y fuerte potencial de rendimiento de alquiler.",
      intro2: "En Nova Iberian, compartimos nuestra experiencia en el mercado inmobiliario español con usted. Ya sea que esté buscando su propio espacio de vida o una propiedad de inversión, estamos aquí para encontrar el inmueble más adecuado para usted.",
      sections: [
        {
          title: "Nuestros Servicios",
          subsections: [
            {
              name: "Investigación y Selección de Propiedades",
              desc: "Proporcionamos acceso a apartamentos de lujo, villas y proyectos de inversión en toda Barcelona y Cataluña.",
              items: ["Apartamentos residenciales de lujo", "Villas independientes y casas de verano", "Proyectos de inversión", "Propiedades de alquiler a corto plazo y turísticas"]
            },
            {
              name: "Due Diligence (Revisión Legal)",
              desc: "Realizamos una revisión legal completa antes de comprar una propiedad:",
              items: ["Verificación de títulos y registros de propiedad", "Control de hipotecas y gravámenes", "Confirmación de permisos y licencias municipales", "Determinación de obligaciones fiscales"]
            },
            {
              name: "Gestión del Proceso de Compra",
              items: ["Obtención del número NIE", "Apertura de cuenta bancaria en España", "Preparación y revisión del contrato de compraventa", "Procedimientos notariales y transferencia de título", "Coordinación de pagos de impuestos y tasas"]
            },
            {
              name: "Consultoría de Inversión",
              desc: "Analizando tendencias y oportunidades en el mercado inmobiliario español, le ofrecemos opciones de inversión con el mayor potencial de retorno.",
              items: ["Análisis de rendimiento de alquiler", "Evaluación del mercado regional", "Planificación de estrategia de inversión a largo plazo", "Recomendaciones de diversificación de cartera"]
            }
          ]
        },
        {
          title: "¿Por qué Barcelona y Cataluña?",
          content: "Barcelona es una de las ciudades más atractivas de Europa. Con playas de clase mundial, rica cultura gastronómica, más de 2.400 horas de sol al año e infraestructura de transporte efectiva, es un destino perfecto tanto para vivir como para invertir."
        },
        {
          title: "Nuestro Proceso",
          steps: [
            { num: "1", title: "Evaluación de Necesidades", desc: "Escuchamos su presupuesto, preferencias y objetivos de inversión." },
            { num: "2", title: "Presentación de Propiedades", desc: "Investigamos y presentamos opciones que cumplen sus criterios." },
            { num: "3", title: "Inspección en Sitio", desc: "Organizamos visitas a propiedades e introducciones de área." },
            { num: "4", title: "Revisión Legal", desc: "Llevamos a cabo el proceso de due diligence." },
            { num: "5", title: "Compra", desc: "Gestionamos los procedimientos de contrato, notario y título." },
            { num: "6", title: "Post-Compra", desc: "Proporcionamos soporte en gestión de propiedades, alquiler y asuntos fiscales." }
          ]
        }
      ],
      metaTitle: "Inversión Inmobiliaria España | Consultoría Barcelona | Nova Iberian",
      metaDesc: "Invierta en bienes raíces en Barcelona y Cataluña. Asesoramiento experto en apartamentos de lujo, villas y proyectos de inversión."
    },
    "oturum-izni": {
      title: "Permiso de Residencia y Visa",
      subtitle: "Vivir y Trabajar Legalmente en España",
      intro: "España atrae a personas de todo el mundo con sus diversas opciones de visa y permiso de residencia. Desde nómadas digitales hasta jubilados, desde estudiantes hasta aquellos que quieren reunir a sus familias, hay un camino legal adecuado para todos.",
      intro2: "En Nova Iberian, con nuestra experiencia en la ley de inmigración española, determinamos el tipo de visa correcto para usted y gestionamos su proceso de solicitud por completo.",
      sections: [
        {
          title: "Visa de Nómada Digital",
          subtitle: "Trabaje Remotamente desde España, Disfrute de la Vida Mediterránea",
          content: "La Visa de Nómada Digital ofrecida bajo la Ley de Startups de España otorga a los profesionales que trabajan remotamente para una empresa en el extranjero el derecho a vivir legalmente en España.",
          highlights: [
            { label: "Tiempo de procesamiento", value: "20 días hábiles" },
            { label: "Duración", value: "3 años" },
            { label: "Miembros de familia", value: "Pueden incluirse" },
            { label: "Ventaja fiscal", value: "Bajo la Ley Beckham" }
          ],
          requirements: ["Profesionales trabajando al menos 3 meses en una empresa con al menos 1 año de historia", "Aquellos que ganan al menos el 80% de ingresos fuera de España", "Ingreso mensual mínimo: 200% del SMI (aproximadamente 2.763 €/mes)", "Título universitario o al menos 3 años de experiencia en el campo", "Seguro de salud privado completo"],
          note: "Los titulares de Visa de Nómada Digital pueden beneficiarse de la Ley Beckham. Impuesto sobre la renta fijo del 24%, válido por 6 años."
        },
        {
          title: "Visa No Lucrativa (Permiso de Residencia Sin Fines de Lucro)",
          subtitle: "Disfrute Vivir en España Sin Trabajar",
          content: "La Visa No Lucrativa es un permiso de residencia diseñado para aquellos que desean vivir en España sin trabajar. Generalmente preferida por jubilados, personas con ahorros o aquellos con ingresos pasivos.",
          highlights: [
            { label: "Tiempo de procesamiento", value: "Aproximadamente 1 mes" },
            { label: "Período inicial", value: "1 año" },
            { label: "Solicitud", value: "Desde el consulado español en su país" },
            { label: "Renovación", value: "En períodos de 2 años" }
          ],
          requirements: ["400% del IPREM en cuenta bancaria (aproximadamente 28.800 €)", "100% adicional del IPREM por miembro de familia", "Extractos bancarios de 6-12 meses", "Seguro de salud privado completo"],
          note: "Los derechos de residencia permanente se adquieren después de 5 años."
        },
        {
          title: "Visa de Estudiante y Educación",
          subtitle: "Gane Experiencia Europea Mientras Estudia en España",
          content: "España es un destino atractivo para estudiantes internacionales con sus universidades de clase mundial, escuelas de idiomas y programas de formación profesional.",
          highlights: [
            { label: "Programa educativo", value: "Al menos 20 horas por semana" },
            { label: "Permiso de trabajo", value: "Hasta 30 horas por semana" },
            { label: "Requisito financiero", value: "Aproximadamente 7.200 € anuales" }
          ],
          requirements: ["Carta de aceptación de una institución educativa acreditada", "Recursos financieros equivalentes al 100% del IPREM mensual durante la educación", "Seguro de salud privado"],
          benefits: ["Los estudiantes universitarios pueden trabajar hasta 30 horas por semana", "Pueden hacer la transición a permiso de trabajo después de graduarse", "Derecho a visa de búsqueda de empleo hasta por 2 años"]
        },
        {
          title: "Visa de Reagrupación Familiar",
          subtitle: "Reúna a su Familia en España",
          content: "Si está viviendo legalmente en España y ha renovado su permiso de residencia, puede traer a los miembros de su familia para que se unan a usted.",
          highlights: [
            { label: "Requisito", value: "Al menos 1 año de residencia legal + permiso de residencia renovado" },
            { label: "Cobertura", value: "Cónyuge, hijos menores de 18, padres dependientes mayores de 65" },
            { label: "Permiso de trabajo", value: "Válido inmediatamente para miembros de familia" }
          ],
          requirements: ["Informe de adecuación de vivienda del municipio", "Ingresos: 150% del IPREM para el primer miembro de familia", "Seguro de salud privado"]
        },
        {
          title: "Ciudadanía Española",
          subtitle: "Conviértase en Ciudadano Español, Obtenga un Pasaporte de la UE",
          content: "Puede solicitar la ciudadanía después de residir legalmente en España durante un período suficiente.",
          highlights: [
            { label: "Camino estándar", value: "10 años de residencia legal ininterrumpida" },
            { label: "Latinoamericanos", value: "2 años" },
            { label: "Matrimonio con español", value: "1 año" }
          ],
          requirements: ["DELE A2: Examen de competencia en idioma español", "CCSE: Examen de constitución y conocimiento cultural español"]
        }
      ],
      metaTitle: "Permiso de Residencia España | Nómada Digital, No Lucrativo | Nova Iberian",
      metaDesc: "Asesoramiento experto para permiso de residencia en España. Visa Nómada Digital, Visa No Lucrativa, Visa de Estudiante y Reagrupación Familiar."
    },
    "kapsamli-danismanlik": {
      title: "Consultoría Integral",
      subtitle: "Gestionamos su Reubicación a España de Manera Holística",
      intro: "Mudarse a España requiere mucho más que centrarse en un solo tema. Muchos procesos interconectados como permisos de residencia, planificación fiscal, inversión inmobiliaria, constitución de empresa, selección de escuela y arreglos de vida diaria deben gestionarse de manera coordinada.",
      intro2: "El servicio de Consultoría Integral de Nova Iberian ofrece un enfoque holístico que combina todos estos procesos bajo un mismo techo.",
      sections: [
        {
          title: "¿Qué Ofrecemos?",
          subsections: [
            {
              name: "Planificación Pre-Mudanza",
              desc: "Creamos una hoja de ruta personalizada evaluando sus necesidades, presupuesto y condiciones especiales antes de mudarse a España."
            },
            {
              name: "Coordinación de Múltiples Procesos",
              desc: "Su tipo de permiso de residencia afecta directamente su régimen fiscal, su inversión inmobiliaria afecta su solicitud de visa, su constitución de empresa afecta su permiso de trabajo. Establecemos estas conexiones desde el principio para asegurar que los procesos se apoyen mutuamente."
            }
          ]
        },
        {
          title: "Áreas de Servicio",
          items: ["Estrategia de visa y permiso de residencia", "Planificación fiscal y evaluación de la Ley Beckham", "Investigación inmobiliaria y consultoría de inversión", "Constitución de empresa y selección de estructura empresarial", "Investigación de escuelas para la familia", "Introducción de área y selección de espacio de vida", "Apertura de cuenta bancaria y configuración de infraestructura financiera", "Selección de seguro de salud", "Registro municipal (Empadronamiento) y procedimientos administrativos"]
        },
        {
          title: "¿Para Quién Es?",
          items: ["Individuos y familias que planean mudarse a España", "Aquellos que necesitan soporte simultáneo en múltiples áreas de servicio", "Aquellos que buscan un único punto de contacto ante la complejidad de los procesos", "Aquellos que recién comienzan a explorar España y no saben por dónde empezar"]
        },
        {
          title: "Nuestro Proceso",
          steps: [
            { num: "1", title: "Reunión de Introducción", desc: "Escuchamos su situación, objetivos y prioridades." },
            { num: "2", title: "Desarrollo de Estrategia", desc: "Preparamos una hoja de ruta y cronograma personalizado para usted." },
            { num: "3", title: "Ejecución Simultánea", desc: "Iniciamos todos los procesos relevantes de manera coordinada." },
            { num: "4", title: "Informes Regulares", desc: "Le mantenemos informado en cada etapa y aclaramos los próximos pasos." },
            { num: "5", title: "Post-Asentamiento", desc: "Soporte continuo que necesita en su nueva vida en España." }
          ]
        }
      ],
      metaTitle: "Consultoría Reubicación España | Relocation Integral | Nova Iberian",
      metaDesc: "Gestionamos todo su proceso de reubicación a España. Visa, impuestos, inmuebles, constitución de empresa bajo un mismo techo."
    },
    "vergi-danismanligi": {
      title: "Consultoría y Planificación Fiscal",
      subtitle: "Maximice sus Ventajas Fiscales en España",
      intro: "El sistema fiscal de España ofrece ventajas significativas cuando se planifica correctamente. Regulaciones especiales como la Ley Beckham pueden reducir significativamente la carga fiscal, especialmente para los nuevos residentes.",
      intro2: "En Nova Iberian, con nuestro servicio de consultoría fiscal en España, optimizamos su situación financiera y nos aseguramos de que cumpla plenamente con sus obligaciones legales.",
      sections: [
        {
          title: "Nuestros Servicios",
          subsections: [
            {
              name: "Ley Beckham (Régimen Fiscal Especial)",
              desc: "Esta es la ventaja fiscal más importante ofrecida a personas que se mudan recientemente a España. En lugar de tasas de impuesto sobre la renta que pueden alcanzar hasta el 47% en condiciones normales, se aplica un impuesto fijo del 24%.",
              items: ["Cobertura: Ingresos hasta 600.000 €", "Duración: 6 años", "Condición: No ser residente fiscal en España en los últimos 5 años", "Válido para titulares de Visa de Nómada Digital"]
            },
            {
              name: "Declaración del Impuesto sobre la Renta (IRPF)",
              desc: "Todos los que son residentes fiscales en España están obligados a presentar una declaración anual del impuesto sobre la renta.",
              items: ["Preparación y revisión de declaración", "Evaluación de deducciones y exenciones fiscales", "Presentación oportuna de declaración"]
            },
            {
              name: "Impuesto de Sociedades",
              desc: "Las empresas que operan en España pagan impuesto de sociedades a una tasa del 25% sobre sus beneficios netos.",
              items: ["Pagos trimestrales a cuenta", "Declaración anual del impuesto de sociedades", "Planificación y optimización fiscal"]
            },
            {
              name: "Servicios de IVA",
              desc: "La tasa estándar de IVA en España es del 21%.",
              items: ["Registro de IVA", "Declaraciones trimestrales de IVA", "Resumen anual de IVA", "Procesos de devolución de IVA"]
            }
          ]
        },
        {
          title: "¿Por qué Nova Iberian?",
          benefits: ["Planificación fiscal integrada con proceso de permiso de residencia", "Soporte de solicitud y seguimiento de Ley Beckham", "Servicio en turco, inglés y español", "Experiencia en asuntos fiscales internacionales", "Soporte continuo de contabilidad y cumplimiento"]
        }
      ],
      metaTitle: "Consultoría Fiscal España | Ley Beckham | Nova Iberian",
      metaDesc: "Benefíciese de las ventajas fiscales en España. Ley Beckham, impuesto sobre la renta, impuesto de sociedades y consultoría de IVA."
    },
    "pilotaj-egitimi": {
      title: "Consultoría de Formación de Pilotos",
      subtitle: "Formación de Pilotos en España: Su Camino al Cielo",
      intro: "España es un destino ideal para la formación de pilotos con su clima soleado durante la mayor parte del año, escuelas de vuelo modernas e instituciones acreditadas que ofrecen formación según los estándares de la Agencia Europea de Seguridad Aérea (EASA).",
      intro2: "Las licencias EASA son válidas en todos los países de la Unión Europea y abren las puertas a una carrera de aviación internacional.",
      sections: [
        {
          title: "Nuestros Servicios",
          subsections: [
            {
              name: "Investigación y Selección de Escuelas de Vuelo",
              desc: "Investigamos escuelas de vuelo aprobadas por EASA en España, comparamos programas de formación y le ayudamos a identificar la escuela más adecuada para sus necesidades.",
              items: ["Revisión de acreditación escolar y registros de seguridad", "Evaluación del currículo de formación y flota de aeronaves", "Comparación de costos e información del plan de pago", "Tasas de éxito de graduados y programas de apoyo profesional"]
            },
            {
              name: "Programas de Formación",
              items: ["PPL (Licencia de Piloto Privado)", "CPL (Licencia de Piloto Comercial)", "ATPL (Licencia de Piloto de Transporte de Línea Aérea)", "Formación de habilitación de tipo"]
            },
            {
              name: "Soporte de Visa y Permiso de Residencia",
              desc: "Determinamos el tipo de visa apropiado para candidatos que vienen a España para formación de piloto y proporcionamos soporte durante el proceso de solicitud."
            },
            {
              name: "Arreglos de Vida en España",
              items: ["Investigación de vivienda", "Selección de seguro de salud", "Introducción de área y orientación", "Apertura de cuenta bancaria y procedimientos administrativos"]
            }
          ]
        },
        {
          title: "¿Por qué Formación de Pilotos en España?",
          benefits: ["Más de 300 días de clima de vuelo adecuado por año", "Formación estándar EASA – licencia válida en todos los países de la UE", "Entre los costos de formación más competitivos de Europa", "Experiencia de vuelo en diversas condiciones climáticas y geografías", "Alta calidad de vida y entorno seguro"]
        },
        {
          title: "Nuestro Proceso",
          steps: [
            { num: "1", title: "Evaluación", desc: "Escuchamos sus objetivos de aviación, presupuesto y plan de tiempo." },
            { num: "2", title: "Emparejamiento de Escuelas", desc: "Presentamos las escuelas de vuelo más adecuadas para usted." },
            { num: "3", title: "Soporte de Solicitud", desc: "Proporcionamos orientación en los procesos de registro escolar y solicitud de visa." },
            { num: "4", title: "Asentamiento", desc: "Coordinamos su vivienda, seguro y procedimientos administrativos en España." },
            { num: "5", title: "Seguimiento", desc: "Estamos a su lado para el soporte que necesita durante la formación." }
          ]
        }
      ],
      metaTitle: "Formación Pilotos España | Licencia EASA | Nova Iberian",
      metaDesc: "¿Quiere obtener formación de piloto en España? Escuelas de vuelo aprobadas por EASA, programas PPL, CPL, ATPL."
    }
  }
};

// Shared Navigation Component
const ServiceNavigation = ({ lang, setLang, scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const t = translations[lang];

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} data-testid="navbar">
      <div className="nav-inner">
        <Link to="/" className="nav-logo" data-testid="nav-logo">
          <img src={LOGO_ICON} alt="Nova Iberian" className="nav-logo-icon" />
          <span className="nav-logo-text">Nova <span>Iberian</span></span>
        </Link>
        
        <div className="nav-links">
          <Link to="/#hizmetler" data-testid="nav-services">{t.nav.services}</Link>
          <Link to="/can-cagatay-celikkol" data-testid="nav-about">{t.nav.about}</Link>
          <Link to="/#referanslar" data-testid="nav-testimonials">{t.nav.testimonials}</Link>
          <Link to="/#ispanya-bilgisi" data-testid="nav-guide">{t.nav.guide}</Link>
          <Link to="/blog" data-testid="nav-blog">{t.nav.blog}</Link>
          <Link to="/#iletisim" data-testid="nav-contact">{t.nav.contact}</Link>
          
          <div className="nav-social-icons">
            <a href="https://x.com/IberianNov846" target="_blank" rel="noopener noreferrer"><TwitterIcon /></a>
            <a href="https://www.instagram.com/nova_iberian" target="_blank" rel="noopener noreferrer"><InstagramIcon /></a>
            <a href="https://youtube.com/@cancagataycelikkol?si=dDAtv-bmmLjSZ-FW" target="_blank" rel="noopener noreferrer"><YouTubeIcon /></a>
            <a href="https://www.linkedin.com/company/nova-iberian-consulting-group-limited-company/" target="_blank" rel="noopener noreferrer"><LinkedInIcon /></a>
            <a href="https://www.tiktok.com/@nova_iberian" target="_blank" rel="noopener noreferrer"><TikTokIcon /></a>
          </div>
        </div>
        
        <div className="nav-right">
          <div className="lang-dropdown" data-testid="lang-switcher">
            <button 
              className="lang-dropdown-btn"
              onClick={() => setLangDropdownOpen(!langDropdownOpen)}
              data-testid="lang-dropdown-btn"
            >
              <span className="lang-code">{lang === 'tr' ? 'TR' : lang === 'en' ? 'EN' : 'ES'}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </button>
            {langDropdownOpen && (
              <div className="lang-dropdown-menu">
                <button 
                  className={`lang-dropdown-item ${lang === 'tr' ? 'active' : ''}`}
                  onClick={() => { setLang('tr'); setLangDropdownOpen(false); }}
                >
                  Türkçe
                </button>
                <button 
                  className={`lang-dropdown-item ${lang === 'en' ? 'active' : ''}`}
                  onClick={() => { setLang('en'); setLangDropdownOpen(false); }}
                >
                  English
                </button>
                <button 
                  className={`lang-dropdown-item ${lang === 'es' ? 'active' : ''}`}
                  onClick={() => { setLang('es'); setLangDropdownOpen(false); }}
                >
                  Español
                </button>
              </div>
            )}
          </div>
          <Link to="/#randevu" className="btn-nav" data-testid="nav-cta">{t.nav.appointment}</Link>
          
          <button 
            className="mobile-menu-btn" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
      
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`} data-testid="mobile-menu">
        <div className="mobile-social-icons">
          <a href="https://x.com/IberianNov846" target="_blank" rel="noopener noreferrer"><TwitterIcon /></a>
          <a href="https://www.instagram.com/nova_iberian" target="_blank" rel="noopener noreferrer"><InstagramIcon /></a>
          <a href="https://youtube.com/@cancagataycelikkol?si=dDAtv-bmmLjSZ-FW" target="_blank" rel="noopener noreferrer"><YouTubeIcon /></a>
          <a href="https://www.linkedin.com/company/nova-iberian-consulting-group-limited-company/" target="_blank" rel="noopener noreferrer"><LinkedInIcon /></a>
          <a href="https://www.tiktok.com/@nova_iberian" target="_blank" rel="noopener noreferrer"><TikTokIcon /></a>
        </div>
        <Link to="/#hizmetler" onClick={() => setMobileMenuOpen(false)}>{t.nav.services}</Link>
        <Link to="/can-cagatay-celikkol" onClick={() => setMobileMenuOpen(false)}>{t.nav.about}</Link>
        <Link to="/#referanslar" onClick={() => setMobileMenuOpen(false)}>{t.nav.testimonials}</Link>
        <Link to="/#ispanya-bilgisi" onClick={() => setMobileMenuOpen(false)}>{t.nav.guide}</Link>
        <Link to="/blog" onClick={() => setMobileMenuOpen(false)}>{t.nav.blog}</Link>
        <Link to="/#iletisim" onClick={() => setMobileMenuOpen(false)}>{t.nav.contact}</Link>
      </div>
    </nav>
  );
};

// Service Detail Page Component
export const ServiceDetailPage = ({ lang, setLang }) => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const initialLangSynced = useRef(false);
  const prevLangRef = useRef(lang);
  
  // Get canonical ID from current slug (supports all languages)
  // Handle legacy /hizmet/ prefix URLs
  let effectiveSlug = slug;
  if (!effectiveSlug) {
    const pathParts = location.pathname.split('/').filter(Boolean);
    // If path is /hizmet/slug, take the last part
    effectiveSlug = pathParts[pathParts.length - 1];
  }
  const canonicalId = getCanonicalIdFromSlug(effectiveSlug);
  
  // Determine the correct language from the URL slug
  const slugMapping = findSlugMapping(effectiveSlug);
  const urlLang = slugMapping?.currentLang;
  
  // Sync language state with URL-detected language ONLY on initial load
  useEffect(() => {
    if (!initialLangSynced.current && urlLang && urlLang !== lang) {
      setLang(urlLang);
    }
    initialLangSynced.current = true;
  }, [urlLang, lang, setLang]);
  
  // Use the synced language for everything
  const t = translations[lang];
  
  // Get service data for current language
  const serviceData = serviceDetails[lang]?.[canonicalId];
  
  // Get the correct slug for current language
  const localizedSlug = getLocalizedSlug(canonicalId, lang);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug, location.pathname]);

  // Redirect to correct language URL ONLY when user manually changes language
  useEffect(() => {
    // Only redirect if language actually changed (user clicked language button)
    if (prevLangRef.current !== lang) {
      // Don't redirect legacy /hizmet/ URLs
      const isLegacyUrl = location.pathname.startsWith('/hizmet/');
      if (!isLegacyUrl && localizedSlug && effectiveSlug !== localizedSlug) {
        navigate(`/${localizedSlug}`, { replace: true });
      }
    }
    prevLangRef.current = lang;
  }, [lang, localizedSlug, effectiveSlug, location.pathname, navigate]);

  // Use detected language from URL for meta tags (SEO)
  const metaLang = urlLang || lang;
  const metaServiceData = serviceDetails[metaLang]?.[canonicalId];
  const metaLocalizedSlug = getLocalizedSlug(canonicalId, metaLang);

  // If service not found, redirect to home
  if (!serviceData) {
    return (
      <div className="app" data-testid="service-not-found">
        <ServiceNavigation lang={lang} setLang={setLang} scrolled={scrolled} />
        <div className="service-detail-page">
          <div className="service-detail-container">
            <h1>{lang === 'tr' ? 'Hizmet Bulunamadı' : lang === 'en' ? 'Service Not Found' : 'Servicio No Encontrado'}</h1>
            <Link to="/" className="btn-primary">
              {lang === 'tr' ? 'Ana Sayfaya Dön' : lang === 'en' ? 'Back to Home' : 'Volver al Inicio'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app" data-testid="service-detail-page">
      <Helmet>
        <title>{metaServiceData?.metaTitle || serviceData.metaTitle}</title>
        <meta name="description" content={metaServiceData?.metaDesc || serviceData.metaDesc} />
        <link rel="canonical" href={`https://novaiberian.com/${metaLocalizedSlug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaServiceData?.metaTitle || serviceData.metaTitle} />
        <meta property="og:description" content={metaServiceData?.metaDesc || serviceData.metaDesc} />
        <meta property="og:url" content={`https://novaiberian.com/${metaLocalizedSlug}`} />
        <meta property="og:image" content="https://novaiberian.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@IberianNov846" />
        <meta name="twitter:title" content={metaServiceData?.metaTitle || serviceData.metaTitle} />
        <meta name="twitter:description" content={metaServiceData?.metaDesc || serviceData.metaDesc} />
        <meta name="twitter:image" content="https://novaiberian.com/og-image.png" />
      </Helmet>

      <ServiceNavigation lang={lang} setLang={setLang} scrolled={scrolled} />

      <main className="service-detail-page" data-testid={`service-${slug}`}>
        {/* Hero Section */}
        <section className="service-hero">
          <div className="service-hero-content">
            <Link to="/#hizmetler" className="service-back-link" data-testid="back-to-services">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              {lang === 'tr' ? 'Tüm Hizmetler' : lang === 'en' ? 'All Services' : 'Todos los Servicios'}
            </Link>
            <h1 data-testid="service-title">{serviceData.title}</h1>
            <p className="service-subtitle">{serviceData.subtitle}</p>
          </div>
        </section>

        {/* Content Section */}
        <section className="service-content">
          <div className="service-content-inner">
            {/* Introduction */}
            <div className="service-intro">
              <p>{serviceData.intro}</p>
              <p>{serviceData.intro2}</p>
            </div>

            {/* Sections */}
            {(serviceData.sections || []).map((section, sectionIdx) => (
              <div key={sectionIdx} className="service-section" data-testid={`service-section-${sectionIdx}`}>
                <h2>{section.title}</h2>
                {section.subtitle && <p className="section-subtitle">{section.subtitle}</p>}
                {section.content && <p className="section-content">{section.content}</p>}
                
                {/* Highlights */}
                {section.highlights && (
                  <div className="service-highlights">
                    {section.highlights.map((h, idx) => (
                      <div key={idx} className="highlight-item">
                        <span className="highlight-label">{h.label}</span>
                        <span className="highlight-value">{h.value}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Requirements */}
                {section.requirements && (
                  <div className="service-requirements">
                    <h4>{lang === 'tr' ? 'Gereksinimler' : lang === 'en' ? 'Requirements' : 'Requisitos'}</h4>
                    <ul>
                      {section.requirements.map((req, idx) => (
                        <li key={idx}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Benefits */}
                {section.benefits && (
                  <div className="service-benefits">
                    <ul>
                      {section.benefits.map((benefit, idx) => (
                        <li key={idx}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Items List */}
                {section.items && (
                  <ul className="service-items-list">
                    {section.items.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                )}

                {/* Note */}
                {section.note && (
                  <div className="service-note">
                    <p>{section.note}</p>
                  </div>
                )}

                {/* Subsections */}
                {section.subsections && section.subsections.map((sub, subIdx) => (
                  <div key={subIdx} className="service-subsection">
                    <h3>{sub.name}</h3>
                    {sub.desc && <p>{sub.desc}</p>}
                    {sub.items && (
                      <ul>
                        {sub.items.map((item, itemIdx) => (
                          <li key={itemIdx}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {sub.note && <p className="subsection-note">{sub.note}</p>}
                  </div>
                ))}

                {/* Steps */}
                {section.steps && (
                  <div className="service-steps">
                    {section.steps.map((step, stepIdx) => (
                      <div key={stepIdx} className="step-item">
                        <div className="step-number">{step.num}</div>
                        <div className="step-content">
                          <h4>{step.title}</h4>
                          <p>{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* CTA Section */}
            <div className="service-cta-section">
              <h3>
                {lang === 'tr' ? 'Daha fazla bilgi almak ister misiniz?' : 
                 lang === 'en' ? 'Would you like more information?' : 
                 '¿Le gustaría más información?'}
              </h3>
              <p>
                {lang === 'tr' ? 'Uzmanlarımızla görüşün ve size özel çözümler üretelim.' : 
                 lang === 'en' ? 'Talk to our experts and let us create customized solutions for you.' : 
                 'Hable con nuestros expertos y permítanos crear soluciones personalizadas para usted.'}
              </p>
              <Link to="/#randevu" className="btn-primary" data-testid="service-cta">
                {lang === 'tr' ? 'Randevu Al' : lang === 'en' ? 'Book Appointment' : 'Reservar Cita'} →
              </Link>
            </div>

            {/* Back to Services Link (Bottom) */}
            <div className="service-bottom-nav">
              <Link to="/#hizmetler" className="service-back-link" data-testid="back-to-services-bottom">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                {lang === 'tr' ? 'Tüm Hizmetler' : lang === 'en' ? 'All Services' : 'Todos los Servicios'}
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-inner">
          <Link to="/" className="footer-logo">
            <img src={LOGO_ICON} alt="Nova Iberian" className="footer-logo-icon" />
            <span className="footer-logo-text">Nova <span>Iberian</span></span>
          </Link>
          <div className="footer-text">© 2026 Nova Iberian. {t.footer.rights}</div>
          <div className="footer-links">
            <Link to="/aydinlatma-metni">{lang === 'tr' ? 'Aydınlatma Metni' : lang === 'en' ? 'Privacy Notice' : 'Aviso de Privacidad'}</Link>
            <Link to="/cerez-politikasi">{lang === 'tr' ? 'Çerez Politikası' : lang === 'en' ? 'Cookie Policy' : 'Política de Cookies'}</Link>
          </div>
        </div>
      </footer>
      
      <CookieBanner lang={lang} />
    </div>
  );
};
